import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {DomSanitizer} from "@angular/platform-browser";
import {ImageCropperComponent, ImageTransform} from "ngx-image-cropper";
import {TranslateModule} from "@ngx-translate/core";
import {NgClass, NgIf} from "@angular/common";
import {SecondaryButtonComponent} from "@cybfed/shared/components/secondary-button/secondary-button.component";
import {PrimaryButtonComponent} from "@cybfed/shared/components/primary-button/primary-button.component";
import {PopupHelperService} from "@cybfed/core/services/popup-helper.service";
import {EventManagerService} from "@cybfed/core/services/event-manager.service";
import {take} from "rxjs";

export enum EFileTypes {
  PNG = 'image/png',
  GIF = 'image/gif',
  SGV = 'image/svg',
  JPEG = 'image/jpeg',
  JPG = 'image/jpeg',
  WEBP = 'image/webp',
  MP4 = 'video/mp4',
  XM4V = 'video/x-m4v',
  GLOBALVIDEO = 'video/*',
  GLOBALAUDIO = 'audio/*',
  GLOBALIMAGE = 'image/x-png,image/png,image/gif,image/svg,image/jpeg,image/webp',
  GIFGLOBAL = '.gif'
}

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  standalone: true,
  imports: [
    TranslateModule,
    ImageCropperComponent,
    NgClass,
    NgIf,
    SecondaryButtonComponent,
    PrimaryButtonComponent
  ],
  styleUrls: ['./file-uploader.component.scss']
})
export class FileUploaderComponent implements OnInit, OnChanges {


  @ViewChild('fileElement') fileElement: any;

  transform: ImageTransform = {};
  filecontent: any;
  uploadedFile: any;
  imageChangedEvent: any = '';
  croppedImage: any = '';
  showCropper: boolean = false;

  hasFooter: boolean = true;
  @Output() setImageSource = new EventEmitter();
  @Output() setVideoSource = new EventEmitter(); // New event for video
  @Output() showCropperBlock = new EventEmitter();
  @Output() imageSrc = new EventEmitter();
  @Input() defaultImage = '';
  @Input() initialImage = '';
  @Input() showRemove = true;
  @Input() settings : any;
  @Input() cropperSize : any;
  @Input() formats : any = [];

  fileObj: any

  constructor(private sanitizer: DomSanitizer,
              private popupHelperService: PopupHelperService,
              private eventManagerService: EventManagerService,
              ) {

  }

  ngOnInit(): void {
    if(!this.settings && this.cropperSize !== 'original'){
      this.settings ={
        resizeToWidth: 256,
        ratio: 1 / 1,
        roundCropper: false
      }
    }else if (!this.settings.ratio || this.settings.ratio <= 0) {
      this.settings.ratio = 1 / 1; // Ensure ratio is valid
    }

    this.eventManagerService.handleOnDropFileUploadEmitter
        .pipe(take(1))
        .subscribe((file: any) => {
          const mockEvent = { target: { files: [file] } };
          this.fileChangeEvent(mockEvent);
        })

  }



  clickOnFileElement(){
    if(!this.settings){
      this.settings = {
        resizeToWidth: 256,
        ratio: 1 / 1,
        roundCropper: false
      }
    }
    this.fileElement.nativeElement.click();
  }




  ngOnChanges(changes: SimpleChanges): void {
    if(changes['cropperSize'] && changes['cropperSize'].currentValue === 'original' && this.uploadedFile){
      this.imageSrc.emit(this.uploadedFile);
    }
    const selfObj = this;
    if (this.initialImage && this.initialImage.length > 0) {

      selfObj.croppedImage = this.sanitize(this.initialImage);
    } else if (this.defaultImage.length > 0) {
      this.toDataURL(this.defaultImage, (dataUrl:string) => {
        if (!selfObj.croppedImage || !selfObj.showRemove) {
          selfObj.defaultImage = dataUrl;
          selfObj.croppedImage = dataUrl;
        }
      });
    }

  }




  toDataURL(url:string, callback:any) {
    const xhr = new XMLHttpRequest();
    xhr.onload = function() {
      const reader = new FileReader();
      reader.onloadend = function() {
        callback(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  }

  getExtension(filename:string) {
    const parts = filename.split('.');
    return parts[parts.length - 1];
  }

  isImage(filename:string) {
    const ext = this.getExtension(filename);
    switch (ext.toLowerCase()) {
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'bmp':
      case 'png':
      case 'webp':
        return true;
    }
    return false;
  }

  isVideo(filename:string){
    const ext = this.getExtension(filename);
    switch (ext.toLowerCase()) {
      case 'mp4':
      case 'mp3':
      case 'm4v':
      case 'mov':
      case 'avi':
      case 'wmv':
      case 'flv':
      case 'mkv':
      case 'webm':
      case 'audio':
        return true;
      default:
        return false;
    }
  }



  fileChangeEvent(event: any): void {
    let isImage = false;
    let isVideo = false;
    const MAX_SIZE_MB = 10; // Maximum size in MB
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024; // Convert to bytes

    // Handle different event cases
    const fileList = event.target?.files || event.srcElement?.files || event.originalTarget?.files;
    if (!fileList || fileList.length === 0) {
      console.error('No files found in the event.');
      return;
    }



    this.filecontent = fileList;
    this.uploadedFile = fileList[0]
    const file = this.filecontent[0];
    if (file.size > MAX_SIZE_BYTES) {
      this.popupHelperService.printError({text: `File size exceeds the ${MAX_SIZE_MB} MB limit. Please upload a smaller file.`})
      return;
    }
    isImage = this.isImage(file.name);
    isVideo = this.isVideo(file.name);
    if (isImage) {
      if(this.cropperSize === 'original'){
        this.imageSrc.emit(file);
      }else {
        this.showCropper = true;
        this.imageChangedEvent = event;
      }

    } else if (isVideo) {
      this.setVideoSource.emit(file);
    } else {
      // Handle unsupported format
      this.popupHelperService.printError({text: 'This format is not supported for upload.'})
    }
  }



  confirmChangeForImage() {
    this.imageSrc.emit(this.croppedImage);
  }

  hideCropper(){
    this.showCropperBlock.emit(false)
    this.showCropper = false
    this.fileElement.nativeElement.value= '';
  }



  sanitize(url:string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  imageLoaded() {

    this.showCropperBlock.emit(true)
    // show cropper
  }



  loadImageFailed() {
  }

  disableClick() {
    return false;
  }

  imageCropped(image: any) {
    var reader = new FileReader();
    reader.readAsDataURL(image.blob);
    reader.onloadend =  () => {
      var base64String = reader.result;
      //@ts-ignore
      this.croppedImage = base64String.substr(base64String.indexOf(', ') + 1);
    }
    this.fileElement.nativeElement.value= '';
  }

  get acceptFormats(): string {
    if (this.formats && this.formats.length > 0) {
      return this.formats.join(',');
    } else {
      return 'image/x-png,image/gif,image/svg,image/jpeg,image/webp,video/mp4,video/x-m4v,video/*,audio/*,.gif';
    }
  }

}
